import EMAIL_PROVIDER_LIST from '../../Shared/emailProviders.json'
import { normalizeWebsiteDomain } from './websiteDomain'

const RESERVED_EXACT_HOSTS = ['localhost', 'example.com', 'example.org', 'example.net'] as const
const RESERVED_HOST_SUFFIXES = ['.local', '.test', '.invalid', '.example'] as const
const EMAIL_PROVIDER_SET = new Set<string>(EMAIL_PROVIDER_LIST)
const RESERVED_EXACT_HOST_SET = new Set<string>(RESERVED_EXACT_HOSTS)

export type WebsiteResearchIneligibilityReason = 'public_email_provider' | 'reserved_domain'

/**
 * Checks whether a hostname is one of the known public email providers.
 */
export function isEmailProvider(domain: string): boolean {
  return EMAIL_PROVIDER_SET.has(domain.trim().toLowerCase())
}

/**
 * Extracts the domain from an email address.
 */
export function extractDomainFromEmail(email: string): string | null {
  if (!email || typeof email !== 'string') {
    return null
  }

  const trimmed = email.trim().toLowerCase()
  const atIndex = trimmed.lastIndexOf('@')

  if (atIndex === -1 || atIndex === trimmed.length - 1) {
    return null
  }

  const domain = trimmed.substring(atIndex + 1)

  if (domain.indexOf('.') === -1 || domain.length < 4) {
    return null
  }

  return domain
}

/**
 * Returns the reason a host or URL cannot be researched automatically.
 */
export function getWebsiteResearchIneligibilityReason(value: string): WebsiteResearchIneligibilityReason | null {
  const host = normalizeHostLikeValue(value)
  if (!host) {
    return null
  }

  if (isEmailProvider(host)) {
    return 'public_email_provider'
  }

  if (isReservedDomain(host)) {
    return 'reserved_domain'
  }

  return null
}

function normalizeHostLikeValue(value: string): string | null {
  const normalized = normalizeWebsiteDomain(value)
  return normalized === null ? null : normalized.slice('https://'.length).replace(/^www\./, '')
}

function isReservedDomain(host: string): boolean {
  return RESERVED_EXACT_HOST_SET.has(host) || RESERVED_HOST_SUFFIXES.some((suffix) => host.endsWith(suffix))
}
